const MB = 1024 * 1024;

const MAGIC_BYTES_BY_MIME = {
  "application/pdf": [[0x25, 0x50, 0x44, 0x46]],
  "image/png": [
    [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a],
  ],
  "image/jpeg": [[0xff, 0xd8, 0xff]],
};

const EXTENSION_TO_MIME = {
  ".pdf": "application/pdf",
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
};

export const UPLOAD_PRESETS = {
  documento: {
    maxBytes: 10 * MB,
    allowedMimes: ["application/pdf", "image/png", "image/jpeg"],
    allowedExtensions: [".pdf", ".png", ".jpg", ".jpeg"],
    typeLabel: "PDF, PNG ou JPEG",
    checkMagicBytes: true,
  },
  condicionante: {
    maxBytes: 10 * MB,
    allowedMimes: ["application/pdf", "image/png", "image/jpeg"],
    allowedExtensions: [".pdf", ".png", ".jpg", ".jpeg"],
    typeLabel: "PDF, PNG ou JPEG",
    checkMagicBytes: true,
  },
  comprovantePublicacao: {
    maxBytes: 10 * MB,
    allowedMimes: ["application/pdf", "image/png", "image/jpeg"],
    allowedExtensions: [".pdf", ".png", ".jpg", ".jpeg"],
    typeLabel: "PDF, PNG ou JPEG",
    checkMagicBytes: true,
  },
  modeloPublicacao: {
    maxBytes: 10 * MB,
    allowedMimes: ["application/pdf"],
    allowedExtensions: [".pdf"],
    typeLabel: "PDF",
    checkMagicBytes: true,
  },
  pagamento: {
    maxBytes: 50 * MB,
    allowedMimes: ["application/pdf", "image/jpeg", "image/png"],
    allowedExtensions: [".pdf", ".jpg", ".jpeg", ".png"],
    typeLabel: "PDF, JPG ou PNG",
    checkMagicBytes: false,
  },
};

function getFileExtension(fileName) {
  const lastDot = fileName.lastIndexOf(".");
  if (lastDot === -1) return "";
  return fileName.slice(lastDot).toLowerCase();
}

function resolveMimeType(file) {
  const mime = (file.type || "").toLowerCase();
  if (mime) return mime;
  return EXTENSION_TO_MIME[getFileExtension(file.name)] || "";
}

async function readFileHeader(file, length) {
  const slice = file.slice(0, length);
  if (typeof slice.arrayBuffer === "function") {
    return new Uint8Array(await slice.arrayBuffer());
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(slice);
  });
}

async function validateMagicBytes(file, mime) {
  const signatures = MAGIC_BYTES_BY_MIME[mime];
  if (!signatures) return null;

  const maxLen = Math.max(...signatures.map((signature) => signature.length));
  const bytes = await readFileHeader(file, maxLen);

  const matches = signatures.some((signature) =>
    signature.every((byte, index) => bytes[index] === byte),
  );

  if (!matches) {
    return "O conteúdo do arquivo não corresponde ao formato declarado.";
  }

  return null;
}

export async function validateUploadFile(file, options = {}) {
  if (!file) {
    return { valid: false, error: "Nenhum arquivo selecionado." };
  }

  const {
    maxBytes,
    allowedMimes = [],
    allowedExtensions = [],
    typeLabel,
    checkMagicBytes = false,
  } = options;

  if (maxBytes && file.size > maxBytes) {
    const maxMb = Math.round(maxBytes / MB);
    return {
      valid: false,
      error: `O arquivo não pode ter mais de ${maxMb} MB.`,
    };
  }

  const mime = resolveMimeType(file);
  const normalizedMimes = allowedMimes.map((item) => item.toLowerCase());

  if (normalizedMimes.length > 0) {
    if (!mime || !normalizedMimes.includes(mime)) {
      return {
        valid: false,
        error: `Formato de arquivo não suportado. Use ${typeLabel || normalizedMimes.join(", ")}.`,
      };
    }
  }

  if (allowedExtensions.length > 0) {
    const extension = getFileExtension(file.name);
    const normalizedExtensions = allowedExtensions.map((item) =>
      item.toLowerCase(),
    );
    if (!normalizedExtensions.includes(extension)) {
      return {
        valid: false,
        error: `Extensão não permitida. Use ${typeLabel || normalizedExtensions.join(", ")}.`,
      };
    }
  }

  if (checkMagicBytes && mime) {
    const magicError = await validateMagicBytes(file, mime);
    if (magicError) {
      return { valid: false, error: magicError };
    }
  }

  return { valid: true };
}
